import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Sparkles,
  X,
  Send,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Bot,
  User,
  Play,
  RotateCcw,
  Info,
  Pencil,
  RefreshCw,
  Wrench,
  ClipboardList,
  Zap,
  ArrowRight,
  Variable,
  ShieldAlert,
  Hammer,
  Maximize2,
} from "lucide-react";
import { chatWithFlowAI } from "../../apis/flowAPI";
import PlanPreviewModal from "./PlanPreviewModal";
import { normalizePlanForDisplay, getPlanEdgeCount } from "./planPreviewUtils";

/** Appended only in API payload so ChatGPT sees full plan on "Build flow"; UI keeps short assistant text. */
const APPROVED_FLOW_PLAN_MARKER = "\n\n---\nAPPROVED_FLOW_PLAN_JSON\n";

function buildChatContentForApi(message) {
  const base = message.content || "";
  if (message.role === "assistant" && message.plan) {
    return `${base}${APPROVED_FLOW_PLAN_MARKER}${JSON.stringify(
      { plan: message.plan, preview: message.preview ?? null },
      null,
      2
    )}`;
  }
  return base;
}

const NODE_TYPE_COLORS = {
  trigger: "bg-green-100 text-green-700",
  template: "bg-blue-100 text-blue-700",
  textMessage: "bg-sky-100 text-sky-700",
  condition: "bg-orange-100 text-orange-700",
  controller: "bg-amber-100 text-amber-700",
  delay: "bg-gray-100 text-gray-600",
  webhook: "bg-violet-100 text-violet-700",
  interactiveButtons: "bg-indigo-100 text-indigo-700",
  interactiveList: "bg-indigo-100 text-indigo-700",
  interactiveUrl: "bg-indigo-100 text-indigo-700",
  interactiveCarouselUrl: "bg-pink-100 text-pink-700",
  interactiveCarouselButton: "bg-pink-100 text-pink-700",
  aiAskQuestion: "bg-purple-100 text-purple-700",
  captureText: "bg-emerald-100 text-emerald-800",
  aiIntentClassifier: "bg-purple-100 text-purple-700",
  aiEntityExtractor: "bg-purple-100 text-purple-700",
  aiResponseGenerator: "bg-purple-100 text-purple-700",
  aiKnowledgeBaseAnswer: "bg-purple-100 text-purple-700",
  knowledgeBase: "bg-teal-100 text-teal-700",
  assignAgent: "bg-rose-100 text-rose-700",
  transferToFlow: "bg-cyan-100 text-cyan-700",
  end: "bg-red-100 text-red-700",
};

const PlanCard = ({ plan, preview, onBuild, onModify, onOpenPreview }) => {
  if (!plan) return null;
  const normalized = normalizePlanForDisplay(plan);
  const summaryCards = preview?.cards?.length ? preview.cards.slice(0, 3) : null;
  const summarySteps = !summaryCards && normalized.steps.length ? normalized.steps.slice(0, 3) : [];
  const edgeCount = getPlanEdgeCount(plan, preview);

  const canOpenPreview =
    !!preview ||
    (plan.plannedNodes && plan.plannedNodes.length) ||
    (plan.nodeStrategy && plan.nodeStrategy.length) ||
    (plan.steps && plan.steps.length) ||
    normalized.steps.length > 0 ||
    edgeCount > 0 ||
    (plan.connections && plan.connections.length > 0) ||
    (plan.branchingStrategy && plan.branchingStrategy.length > 0);

  const validationList = plan.validationChecklist?.length
    ? plan.validationChecklist
    : plan.validationNotes;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-indigo-50 border border-indigo-200 rounded-xl overflow-hidden">
      {plan.objective && (
        <div className="px-3 py-2 bg-indigo-600 text-white">
          <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Plan Objective</p>
          <p className="text-xs font-semibold">{plan.objective}</p>
        </div>
      )}

      {(summaryCards || summarySteps.length > 0) && (
        <div className="px-3 py-2 border-b border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1.5">
            Preview (
            {preview?.totalPlannedNodes ?? plan.plannedNodes?.length ?? plan.totalNodes ?? normalized.steps.length}{" "}
            nodes
            {edgeCount > 0 ? ` · ${edgeCount} links` : ""})
          </p>
          <div className="space-y-1">
            {summaryCards
              ? summaryCards.map((c, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="shrink-0 w-4 h-4 rounded-full bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center mt-0.5">
                      {c.step || i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          NODE_TYPE_COLORS[c.nodeType] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {c.nodeType}
                      </span>
                      <p className="text-[11px] text-gray-700 mt-0.5 leading-tight font-medium">
                        {c.headline || c.userSummary || c.content}
                      </p>
                      {c.nodeTypeRationale && (
                        <p className="text-[10px] text-indigo-600/90 mt-0.5 leading-tight">{c.nodeTypeRationale}</p>
                      )}
                    </div>
                  </div>
                ))
              : summarySteps.map((s, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span className="shrink-0 w-4 h-4 rounded-full bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center mt-0.5">
                      {s.step || i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          NODE_TYPE_COLORS[s.nodeType] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {s.nodeType}
                      </span>
                      <p className="text-[11px] text-gray-700 mt-0.5 leading-tight">{s.description}</p>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}

      {plan.steps?.length > 0 &&
        !plan.nodeStrategy?.length &&
        !plan.plannedNodes?.length &&
        !summaryCards && (
        <div className="px-3 py-2 border-b border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1.5">
            Nodes ({plan.totalNodes || plan.steps.length})
          </p>
          <div className="space-y-1">
            {plan.steps.slice(0, 3).map((s, i) => (
              <div key={i} className="flex items-start gap-1.5">
                <span className="shrink-0 w-4 h-4 rounded-full bg-indigo-600 text-white text-[9px] font-bold flex items-center justify-center mt-0.5">
                  {s.step || i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <span
                    className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      NODE_TYPE_COLORS[s.nodeType] || "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {s.nodeType}
                  </span>
                  <p className="text-[11px] text-gray-700 mt-0.5 leading-tight">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {plan.connections?.length > 0 && (
        <div className="px-3 py-2 border-b border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">
            Connections ({plan.totalConnections || plan.connections.length})
          </p>
          <div className="space-y-0.5 max-h-20 overflow-y-auto">
            {plan.connections.slice(0, 5).map((c, i) => (
              <div key={i} className="flex items-center gap-1 text-[10px] text-gray-600">
                <span className="font-semibold text-gray-800">{c.from}</span>
                <ArrowRight size={9} className="text-indigo-400 shrink-0" />
                <span className="font-semibold text-gray-800">{c.to}</span>
                {c.condition && c.condition !== "always" && (
                  <span className="text-[9px] px-1 py-0.5 bg-orange-50 text-orange-600 rounded font-medium">
                    {c.condition}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {normalized.variables.length > 0 && (
        <div className="px-3 py-2 border-b border-indigo-100">
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">
            Variables ({normalized.variables.length})
          </p>
          {normalized.variables.slice(0, 4).map((v, i) => (
            <div key={i} className="flex items-start gap-1 text-[10px] mb-0.5">
              <Variable size={10} className="text-purple-500 mt-0.5 shrink-0" />
              <span className="font-mono font-bold text-purple-700">{`{{${v.name}}}`}</span>
              {v.producedBy && <span className="text-gray-400">from {v.producedBy}</span>}
              {v.description && <span className="text-gray-500">— {v.description}</span>}
            </div>
          ))}
        </div>
      )}

      {validationList?.length > 0 && (
        <div className="px-3 py-2 border-b border-indigo-100">
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">
            {plan.validationChecklist?.length ? "Validation checklist" : "Validation notes"}
          </p>
          {validationList.slice(0, 3).map((n, i) => (
            <div key={i} className="flex items-start gap-1 text-[10px] text-amber-700 mb-0.5">
              <ShieldAlert size={10} className="mt-0.5 shrink-0" />
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}

      <div className="px-3 py-2 flex flex-col gap-1.5">
        {canOpenPreview && (
          <button
            type="button"
            onClick={onOpenPreview}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-white border-2 border-indigo-200 text-indigo-700 text-xs font-bold rounded-lg hover:bg-indigo-50 transition-colors"
          >
            <Maximize2 size={12} />
            Open full preview
          </button>
        )}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onBuild}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Hammer size={12} />
            Build This Flow
          </button>
          <button
            type="button"
            onClick={onModify}
            className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-colors"
          >
            <Pencil size={11} />
            Modify
          </button>
        </div>
      </div>
    </div>
  );
};

const AIChatPanel = ({
  visible,
  onClose,
  nodes,
  edges,
  onApplyFlow,
  runValidation,
  validationErrors,
  flowVariables,
}) => {
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "assistant",
      content:
        'Describe the chatbot flow you want to build. I can plan it first for your review, or build it directly.\n\nExamples:\n- "Plan a flow that collects user preferences"\n- "Build a welcome flow with name and email collection"\n- "Fix the validation errors in my flow"',
      action: "explain",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [includeAINodes, setIncludeAINodes] = useState(true);
  /** 'plan' | 'flow' consumed on next send (from quick actions) */
  const pendingResponseModeRef = useRef(null);
  const [planPreviewModal, setPlanPreviewModal] = useState({ open: false, plan: null, preview: null });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (visible) inputRef.current?.focus();
  }, [visible]);

  const buildCurrentFlow = useCallback(() => {
    if (!nodes.length) return null;
    return {
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: (() => {
          const clean = {};
          for (const [k, v] of Object.entries(n.data || {})) {
            if (typeof v !== "function") clean[k] = v;
          }
          return clean;
        })(),
      })),
      connections: edges.map((e) => ({
        source: e.source,
        target: e.target,
        conditionType: e.data?.conditionType || "always",
        conditionValue: e.data?.conditionValue || "",
        sourceHandle: e.sourceHandle || null,
      })),
    };
  }, [nodes, edges]);

  const sendMessage = useCallback(async (text, apiOptions = {}) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const fromRef = pendingResponseModeRef.current;
    if (fromRef) pendingResponseModeRef.current = null;
    const responseMode =
      apiOptions.responseMode !== undefined ? apiOptions.responseMode : fromRef || undefined;

    const userMsg = {
      id: `user-${Date.now()}`,
      role: "user",
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const chatHistory = messages
        .filter((m) => m.id !== "welcome")
        .map((m) => ({ role: m.role, content: buildChatContentForApi(m) }));
      chatHistory.push({ role: "user", content: trimmed });

      const currentFlow = buildCurrentFlow();

      const response = await chatWithFlowAI(chatHistory, currentFlow, {
        includeAINodes,
        validationErrors: validationErrors?.length ? validationErrors : undefined,
        flowVariables: flowVariables?.length ? flowVariables.map(v => ({ name: v.name, description: v.description || "" })) : undefined,
        ...(responseMode ? { responseMode } : {}),
      });

      const data = response.data;
      const aiMsg = {
        id: `ai-${Date.now()}`,
        role: "assistant",
        content: data.message || "Done.",
        action: data.action,
        flow: data.flow || null,
        plan: data.plan || null,
        preview: data.preview ?? null,
        fieldsToFill: data.fieldsToFill || [],
      };
      setMessages((prev) => [...prev, aiMsg]);
    } catch (err) {
      const errMsg = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: `Something went wrong: ${err.response?.data?.error || err.message}. Please try again.`,
        action: "explain",
        isError: true,
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  }, [messages, buildCurrentFlow, includeAINodes, validationErrors, flowVariables]);

  const handleSend = useCallback(() => {
    if (!input.trim() || loading) return;
    sendMessage(input);
  }, [input, loading, sendMessage]);

  const handleApplyFlow = useCallback(
    (flow) => {
      if (!flow) return;
      onApplyFlow(flow);

      const applyMsg = {
        id: `system-${Date.now()}`,
        role: "assistant",
        content: "Flow applied to canvas. Running validation...",
        action: "explain",
        isSystem: true,
      };
      setMessages((prev) => [...prev, applyMsg]);

      setTimeout(() => {
        const result = runValidation();
        if (result && !result.valid) {
          const errSummary = {
            id: `val-${Date.now()}`,
            role: "assistant",
            content: `Validation found ${result.errors.length} error(s) and ${result.warnings.length} warning(s). You can ask me to "fix the errors" or address them manually.`,
            action: "explain",
            isValidation: true,
            validationResult: result,
          };
          setMessages((prev) => [...prev, errSummary]);
        } else {
          const okMsg = {
            id: `val-ok-${Date.now()}`,
            role: "assistant",
            content: "Flow passes validation — no errors found!",
            action: "explain",
            isValidation: true,
          };
          setMessages((prev) => [...prev, okMsg]);
        }
      }, 300);
    },
    [onApplyFlow, runValidation]
  );

  const handleBuildFromPlan = useCallback(() => {
    if (loading) return;
    sendMessage(
      "Build the flow based on the plan above. Generate the complete flow with all nodes, connections, and variables.",
      { responseMode: "flow" }
    );
  }, [loading, sendMessage]);

  const handleModifyPlan = useCallback(() => {
    pendingResponseModeRef.current = "plan";
    setInput("Modify the plan: ");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleFixErrors = useCallback(() => {
    setInput("Fix the validation errors in my current flow");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleEditFlow = useCallback(() => {
    setInput("Modify my current flow: ");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleRebuildFlow = useCallback(() => {
    setInput("Rebuild my entire flow from scratch: ");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handlePlanAndBuild = useCallback(() => {
    pendingResponseModeRef.current = "plan";
    setInput("Plan the flow first, then I'll review before building: ");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleBuildDirectly = useCallback(() => {
    pendingResponseModeRef.current = "flow";
    setInput("Build the flow directly: ");
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const openPlanPreviewForMessage = useCallback((plan, preview) => {
    setPlanPreviewModal({ open: true, plan, preview });
  }, []);

  const closePlanPreviewModal = useCallback(() => {
    setPlanPreviewModal((s) => ({ ...s, open: false }));
  }, []);

  const handleClearChat = useCallback(() => {
    setMessages([
      {
        id: "welcome",
        role: "assistant",
        content: "Chat cleared. Describe what you'd like to build or change.",
        action: "explain",
      },
    ]);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  if (!visible) return null;

  const quickActionBtn = "flex items-center gap-1 px-2.5 py-1.5 bg-white border border-gray-200 text-gray-700 text-[11px] font-semibold rounded-lg hover:bg-gray-50 hover:border-purple-300 transition-colors";

  return (
    <>
    <PlanPreviewModal
      open={planPreviewModal.open}
      onClose={closePlanPreviewModal}
      plan={planPreviewModal.plan}
      preview={planPreviewModal.preview}
      onBuild={handleBuildFromPlan}
      onModify={handleModifyPlan}
    />
    <div className="w-96 bg-white border border-gray-200 rounded-2xl shadow-lg flex flex-col max-h-[calc(100vh-140px)]">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
            <Sparkles className="text-white" size={16} />
          </div>
          <div>
            <h3 className="font-bold text-sm text-gray-900">AI Flow Builder</h3>
            <p className="text-[10px] text-gray-500 font-medium">Chat to build flows</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleClearChat}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            title="Clear chat"
          >
            <RotateCcw size={14} className="text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={16} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* AI Nodes Toggle */}
      <div className="px-3 py-2 border-b border-gray-100 shrink-0">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeAINodes}
            onChange={(e) => setIncludeAINodes(e.target.checked)}
            className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          <span className="text-xs font-semibold text-gray-600">
            Include AI-powered nodes
          </span>
          <Info size={12} className="text-gray-400" title="When unchecked, AI Ask Question, Intent Classifier, Entity Extractor, Response Generator, and KB Answer nodes are excluded" />
        </label>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.map((msg) => (
          <div key={msg.id}>
            {msg.role === "user" ? (
              <div className="flex justify-end">
                <div className="flex items-start gap-2 max-w-[85%]">
                  <div className="bg-purple-600 text-white px-3 py-2 rounded-xl rounded-tr-sm text-sm font-medium whitespace-pre-wrap">
                    {msg.content}
                  </div>
                  <div className="shrink-0 w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center mt-0.5">
                    <User size={12} className="text-purple-600" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-start">
                <div className="flex items-start gap-2 max-w-[90%]">
                  <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                    msg.isError ? "bg-red-100" : msg.isValidation ? "bg-blue-100" : "bg-gradient-to-br from-purple-100 to-indigo-100"
                  }`}>
                    {msg.isError ? (
                      <AlertTriangle size={12} className="text-red-500" />
                    ) : msg.isValidation ? (
                      <CheckCircle2 size={12} className="text-blue-500" />
                    ) : (
                      <Bot size={12} className="text-purple-600" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className={`px-3 py-2 rounded-xl rounded-tl-sm text-sm font-medium whitespace-pre-wrap ${
                      msg.isError
                        ? "bg-red-50 text-red-800 border border-red-200"
                        : msg.isValidation
                        ? "bg-blue-50 text-blue-800 border border-blue-200"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      {msg.content}
                    </div>

                    {/* Quick action buttons after welcome message */}
                    {msg.id === "welcome" && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        <button onClick={handlePlanAndBuild} className={quickActionBtn}>
                          <ClipboardList size={11} className="text-indigo-500" />
                          Plan & Build
                        </button>
                        <button onClick={handleBuildDirectly} className={quickActionBtn}>
                          <Zap size={11} className="text-emerald-500" />
                          Build directly
                        </button>
                        <button onClick={handleEditFlow} className={quickActionBtn}>
                          <Pencil size={11} className="text-purple-500" />
                          Edit flow
                        </button>
                        <button onClick={handleRebuildFlow} className={quickActionBtn}>
                          <RefreshCw size={11} className="text-purple-500" />
                          Rebuild
                        </button>
                        <button onClick={handleFixErrors} className={quickActionBtn}>
                          <Wrench size={11} className="text-purple-500" />
                          Fix errors
                        </button>
                      </div>
                    )}

                    {/* Plan card */}
                    {msg.action === "plan" && msg.plan && (
                      <PlanCard
                        plan={msg.plan}
                        preview={msg.preview}
                        onBuild={handleBuildFromPlan}
                        onModify={handleModifyPlan}
                        onOpenPreview={() => openPlanPreviewForMessage(msg.plan, msg.preview)}
                      />
                    )}

                    {/* Apply Flow button */}
                    {msg.flow && (
                      <button
                        onClick={() => handleApplyFlow(msg.flow)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                      >
                        <Play size={12} />
                        Apply Flow ({msg.flow.nodes?.length || 0} nodes)
                      </button>
                    )}

                    {/* Fields to fill guidance */}
                    {msg.fieldsToFill?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        <p className="text-[10px] font-bold text-amber-700 mb-1 uppercase tracking-wide">
                          Fields to complete:
                        </p>
                        {msg.fieldsToFill.map((f, i) => (
                          <p key={i} className="text-xs text-amber-800">
                            <span className="font-semibold">{f.nodeId}</span>{" "}
                            → {f.field}: {f.description}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Validation errors with fix button */}
                    {msg.isValidation && msg.validationResult && !msg.validationResult.valid && (
                      <button
                        onClick={handleFixErrors}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs font-bold rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Sparkles size={12} />
                        Ask AI to fix errors
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div className="flex justify-start">
            <div className="flex items-start gap-2">
              <div className="shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center mt-0.5">
                <Bot size={12} className="text-purple-600" />
              </div>
              <div className="bg-gray-100 px-4 py-3 rounded-xl rounded-tl-sm">
                <div className="flex items-center gap-1.5">
                  <Loader2 size={14} className="animate-spin text-purple-500" />
                  <span className="text-xs text-gray-500 font-medium">Thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-200 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe your flow or ask to modify..."
            rows={1}
            className="flex-1 px-3 py-2 text-sm font-medium border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 focus:outline-none resize-none max-h-24 overflow-y-auto"
            style={{ minHeight: "38px" }}
            disabled={loading}
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="p-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default AIChatPanel;
